import { Component, DestroyRef, DoCheck, EventEmitter, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT, DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray
} from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgxMaskModule } from 'ngx-mask';
import { VendorService } from '../../vendor.service';
import { ImpersonationService } from '../../../auth/impersonation.service';
import { ItemWriteService } from './item-write.service';
import {
  CATEGORY_DIFFERS_FROM_MASTER,
  EDIT_ERROR_CODES,
  ICloneSource,
  IConflictMark,
  ICreateItemAttribute,
  ICreateItemDiscountQty,
  ICreateItemRequest,
  IEditItemRequest,
  IEditHeader,
  IItemProblemDetails,
  IItemResolution,
  ILookupAttribute,
  ILookupCategory,
  ILookupExwPoint,
  ILookupManufacturer,
  ItemResponse,
  IWriteWarning
} from './item.models';
import { ItemTextModalComponent } from './item-text-modal/item-text-modal.component';
import { IVendorCompany } from '../../../shared/interfaces/model';
import { ItemWithPendingOffers } from '../../../shared/interfaces/offer/Offer';
// The shared write rules (HUBX-8474). Everything below that reads like a rule rather than
// like this modal's own wiring lives in ./rules and is consumed by the two listing surfaces
// too; what stays here is subscription, invocation and the modal's own chrome. Namespace
// imports rather than named ones so a call site says WHICH body of rules it is asking.
import * as statusRules from './rules/item-write-status.rules';
import * as writeAccess from './rules/item-write-writability.rules';
import * as conflictRules from './rules/item-write-concurrency';
import * as validation from './rules/item-write-validators';
import * as copy from './rules/item-write-messages';
import * as editRules from './rules/item-write-edit.rules';
import * as resolveRules from './rules/item-write-resolve.rules';
import { CharCounterComponent, charCounterVisible } from './char-counter/char-counter.component';
import { ConflictResolveComponent } from './conflict-resolve/conflict-resolve.component';
import { createItemWriteForm, DESCRIPTION_MAX_LENGTH } from './rules/item-write-form.factory';

/**
 * Create Item modal (HUBX-8385). Visually mirrors the item-detailed edit popup
 * but is a standalone component with its own form: none of the edit machinery
 * (updatedItem diffing, undo, pending offers) applies to Create.
 *
 * Role behavior (design doc §8.3 / ADR-0009):
 * - Status dropdown maps to the CreateApproved flag only (Active=true, Pending=false).
 *   SELLER: locked to Pending. ADMIN/VM: selectable, with a consequence hint.
 * - XID, Price, DQ #1-3: ADMIN/VM only. MOQ/MXQ: all roles. Cost: all roles.
 * - Restrictions / Special Notes: state chips opening a shared text modal;
 *   empty text means the member is omitted from the request.
 *
 * Phase 3 (this change): validators mirroring the edge/handler contract
 * (CreateItemRequest.cs, design doc §4.1). Requiredness follows the contract as
 * shipped, NOT the coarse layout asterisks:
 * - required: VendorItemCode, Mpn, Description, ManufacturerId, CategoryId,
 *   Condition, Packaging, Warranty, ExwId, Availability, LeadTimeDays, Cost.
 * - Cost (UnitPurchasePrice) is required for EVERY role (PricingPolicy);
 *   UnitPrice is required for ADMIN/VM only and is disabled + never sent for
 *   SELLER (a Seller supplying it is UNIT_PRICE_NOT_ALLOWED, 403).
 * - MOQ is OPTIONAL (§4.1, 2026-07-20: absent -> server default 1); ≥1 if present.
 * - HubxItemCode (XID) is optional, ADMIN/VM-only (disabled for SELLER -> A-R10 default).
 * - The three attributes are the client mirror of the handler's
 *   REQUIRED_ATTRIBUTES_MISSING; the edge floor is only "≥1 attribute".
 * Cross-field mirrors (server is the truth): MOQ≤MXQ (MOQ_EXCEEDS_MXQ),
 * UnitPrice≥Cost (UNIT_PRICE_BELOW_COST), and per DQ tier: complete pair,
 * FromQty≥2 (DISCOUNT_QTY_BELOW_MINIMUM), Price<UnitPrice
 * (DISCOUNT_PRICE_EXCEEDS_UNIT_PRICE). The 3P cost-margin rule
 * (DISCOUNT_PRICE_BELOW_COST_MARGIN) stays server-only — the client cannot know
 * BP group membership reliably.
 *
 * Error presentation follows the item-detailed edit popup: a red ring on each
 * invalid control marks WHERE, and a single prioritized message banner at the
 * bottom of the footer says WHAT.
 *
 * Phase 4: submit() builds ICreateItemRequest (empty optionals omitted, never
 * null; attributes {attributeId, value:code}; createApproved only for ADMIN/VM;
 * SELLER-disabled controls drop out of fg.value), POSTs via ItemWriteService,
 * emits `created` with the new id and submitted code on 201 (parent closes,
 * reloads, and confirms), and routes
 * ProblemDetails by its stable `code` to the mapped control + banner.
 */
@Component({
  selector: 'app-item-write-modal',
  templateUrl: './item-write-modal.component.html',
  styleUrls: ['./item-write-modal.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, NgTemplateOutlet, ReactiveFormsModule, FormsModule, TooltipModule, NgxMaskModule, ItemTextModalComponent, DatePipe, CharCounterComponent, ConflictResolveComponent]
})
export class ItemWriteModalComponent implements OnInit, DoCheck {
  @Output() closed = new EventEmitter<void>();
  /** `categoryNotice` is present only when the write-ack carried the category
   *  divergence advisory (HUBX-8429) — already resolved to display text here,
   *  because the category names live in this component's lookup tree. */
  @Output() created = new EventEmitter<{ id: string; vendorItemCode: string; categoryNotice?: string }>();

  /** Clone (HUBX-8368): when present, the modal is seeded from an existing item.
   *  Set once by the parent before creation (the modal is instantiated behind an
   *  *ngIf), so it is available in ngOnInit. Absent => a blank Create. The seed is
   *  applied AFTER lookups load — category resolution walks the tree and the three
   *  attributes match by attributeId, both of which need the lookups in hand. */
  @Input() seed?: ItemResponse;

  /** Clone (HUBX-8422): the item the seed came FROM, for the header strip. Its
   *  presence is what makes this a clone as far as the template is concerned —
   *  the parent sets it and `seed` together. Display only: nothing here is read
   *  by the form or the request. */
  @Input() source?: ICloneSource;

  /** Edit (HUBX-8388): the item being EDITED, full state as the GET returned it.
   *  Its presence is what makes this modal an Edit — `mode` is derived, never set
   *  separately, so the two cannot disagree.
   *
   *  Deliberately NOT the same input as `seed`, even though both carry an
   *  ItemResponse. Clone's rule is "copy what describes the product, reset what
   *  prices it" (applySeed); Edit's is the opposite — every member travels, money
   *  included, because the body is full state and an omitted value is a real
   *  removal. One input serving both would make that difference a runtime branch
   *  inside the seeding, which is exactly where it would eventually be got wrong. */
  @Input() target?: ItemResponse;

  /** Edit (HUBX-8388): the read-only header facets — promo badges and the two activity
   *  timestamps — built by the parent from the grid row. Display only.
   *
   *  A separate input from `target` because it has a separate SOURCE: `target` is the
   *  edit contract from GET /items/{id}, this is read-model decoration off the row
   *  (HUBX-8436 §6). Folding it in would have meant growing ItemResponse with members
   *  the PUT does not accept. */
  @Input() header?: IEditHeader;

  /** Edit (HUBX-8476): the reactivation threshold of a Removed item, as the v2 listing row
   *  carries it (`IListingRow.reactivationThreshold`, HUBX-8486). Only that source: GET
   *  /items/{id} does not return it, the bands never reach the client, and the legacy row's
   *  `reactivationThreshold` is a different number — it floors by sale price and falls back
   *  to UnitPrice when there is no cost, so it can promise a reactivation v2 never makes.
   *  Until the rows move to IListingRow (HUBX-8480 / HUBX-8481) nobody binds this and the
   *  notice stays off, which is the intended state, not a gap to paper over.
   *
   *  Not folded into `header`: that one is built from the legacy row today. */
  @Input() reactivationThreshold?: number | null;

  /** Edit from the listing (HUBX-8480): the row's unsaved edit, the complete ItemResponse the
   *  page's edited-row store holds for it. Seeded OVER `target`, so the popup shows what the
   *  row shows ("Editing in the row and opening the popup on the same item shows the same
   *  pending edit").
   *
   *  `target` stays the item AS STORED, and with it `origin`: the baseline every status rule
   *  and the 412 comparison read, and the source of the echo-only members. Were the edit the
   *  baseline, a cost the row raised would no longer read as a raise. The tokens come from
   *  `target` as well - the edit carries the same ones (a resolved conflict moves both). What the
   *  edit does carry beyond the form is the archive intent, which the row's Archive button
   *  sets on it.
   *
   *  The edit is unsaved work: the form opens dirty, so Save and Undo are offered as they are
   *  on the row, and Undo takes it back from the row too (`undone`).
   *
   *  Absent when the row is untouched, and always absent outside the listing (pending-sold,
   *  which fetches its target). */
  @Input() pending?: ItemResponse;

  /** Edit from a refused listing row (HUBX-8466): the 400 the row's edit (`pending`) was
   *  refused with. The popup sends that same edit, so it opens as if the 400 were its own:
   *  the field or tier ringed, the line in the banner, Save waiting for a change - which
   *  clears it, as it clears any server error. Read only with `pending`. */
  @Input() refusal?: IItemProblemDetails;

  /** Edit succeeded. Carries the same category advisory Create's `created` does —
   *  resolved to a sentence here, while the lookup tree is still in hand. */
  @Output() saved = new EventEmitter<{ id: string; vendorItemCode: string; categoryNotice?: string; statusNotice?: string }>();

  /** Edit only (HUBX-8511): the save about to be sent would cancel these pending offers. The
   *  page opens its offers warning over the modal and answers with continueSave() (Cancel)
   *  or keepEdit() (Keep, or the warning closed); until then the modal waits, loader on. */
  @Output() offersAffected = new EventEmitter<ItemWithPendingOffers>();

  /** Edit only (HUBX-8480): Undo was pressed, with the item's id. Undo takes back the whole
   *  pending edit of the item, so the listing drops its row's edit too and the row, its
   *  icons and the counters go back with the popup. */
  @Output() undone = new EventEmitter<string>();

  /** Edit only (HUBX-8466): a Save was answered with a 412, with the item's id and the
   *  state the server holds when the 412 carried it. The listing row of this item is just as
   *  stale as the popup, so the page brings it up to date: re-read when the row holds no
   *  edit, marked conflicted when it does. */
  @Output() conflicted = new EventEmitter<{ id: string; currentState?: ItemResponse }>();

  fg: FormGroup;
  loader = false;
  lookupsError = false;
  submitted = false;

  /** Server-side failure message (routed from ProblemDetails.code), shown in the
   *  banner with top priority; cleared on the next edit or submit. */
  serverError: string | null = null;
  /** The banner's failure was the server refusing the data (a 400, HUBX-8466): sent again as it
   *  is, it would be refused again, so Save waits for a change. Goes with the banner
   *  (clearServerError), which every change clears. */
  serverRefused = false;
  /** Edit has revealed every refusal (ngDoCheck), for as long as there is a change to save. */
  private revealed = false;

  manufacturers: ILookupManufacturer[] = [];
  categories: ILookupCategory[] = [];
  subCategories: ILookupCategory[] = [];
  condition: ILookupAttribute;
  packaging: ILookupAttribute;
  warranty: ILookupAttribute;
  exwPoints: ILookupExwPoint[] = [];

  /** Target company (ADR-0010, HUBX-8421). The companies this operator may write
   *  for, from the admin API's impersonation surface — the same source as the
   *  portal's company switcher, read-only. Empty for a SELLER (never fetched) and
   *  empty if the fetch fails, and an empty list hides the control: a failure to
   *  load companies must not block creating for the company you are already in. */
  companies: IVendorCompany[] = [];

  /** The company this session is currently acting as, as the client sees it. Used
   *  only to preselect the target selector's default — NOT to decide what to send.
   *  Recognising a selection as "no retarget" is the server's job against its
   *  authoritative context, which this client-side value (from sessionStorage) can
   *  lag. */
  private ambientVendorId: string | null = null;

  /** Which text field the shared text modal is editing; null = closed. */
  textModalField: 'restrictions' | 'specialNotes' | null = null;

  /** Last answer from GET /items/resolutions for the MPN currently in the form
   *  (HUBX-8430/8431); null before the first answer, on a blank MPN, or when the
   *  call failed. Only the MPN refreshes it — a category change re-reads THIS,
   *  never the network (see categoryNudge). */
  private mpnResolution: IItemResolution | null = null;

  readonly sellerStatusHint = copy.SELLER_STATUS_HINT;
  /** Approved and published, or - on an item the vendor paused - approved and still Paused
   *  (HUBX-8466). */
  get approvalNotice(): string {
    return this.staysPaused ? copy.APPROVAL_NOTICE_STAYS_PAUSED : copy.APPROVAL_NOTICE;
  }

  /** An operator's Active on a Pending or Removed item the vendor paused: the save approves or
   *  reactivates it and it stays Paused (HUBX-8466, ADR-0009 Rule 6). Wording only. */
  get staysPaused(): boolean {
    return this.isEdit && statusRules.staysPausedAfterSave(
      this.fg?.controls.skuStatus.value, this.statusBaseline, this.mayApprove);
  }

  /** The Description counter (shown on focus, and while over the limit). The limit is the
   *  form's validator; the line under the row says why a long text is refused. */
  readonly descriptionMax = DESCRIPTION_MAX_LENGTH;
  descriptionFocused = false;

  get descriptionLength(): number {
    return String(this.fg?.controls['description']?.value ?? '').length;
  }

  get showDescriptionCounter(): boolean {
    return charCounterVisible(this.descriptionFocused, this.descriptionLength, this.descriptionMax);
  }
  readonly reviewNotice = copy.REVIEW_NOTICE;
  get reactivationNotice(): string {
    return copy.withStaysPaused(copy.REACTIVATION_NOTICE, this.staysPaused);
  }

  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  /** Marks the body while the modal is open. The modal renders inside .vendor-home-wrap, a
   *  stacking context at z-index 3 under the page header (z-index 9), so the header covered
   *  the modal's top - its title and X - whatever the modal's own z-index. A global rule
   *  (styles.css) lifts the wrap above the header while this class is on. */
  static readonly OPEN_CLASS = 'item-write-modal-open';

  constructor(
    public vendorService: VendorService,
    private itemCreateService: ItemWriteService,
    private fb: FormBuilder
  ) { }

  get isPricingRole(): boolean {
    return this.vendorService.isAdmin || this.vendorService.isVendorManager;
  }

  /** Edit vs Create, derived from the presence of a target rather than set on its
   *  own — one fact, one source. */
  get isEdit(): boolean {
    return !!this.target;
  }

  /** The baseline every status rule compares against: the item AS READ. It IS `origin`,
   *  which is why the four fields this modal used to keep beside it (the two axes, the
   *  projection and the Available as read) are gone — they were copies of members of the
   *  same object, assigned in the same two places, free to drift from it and from nothing
   *  else. Undefined on a Create, and the rules answer the Create-safe way. */
  private get statusBaseline(): statusRules.IStatusBaseline | undefined {
    return this.origin;
  }

  get assignableStatusOptions(): { value: string; label: string }[] {
    return statusRules.assignableStatusOptions(this.statusBaseline);
  }

  get automaticStatusOptions(): { value: string; label: string }[] {
    return statusRules.automaticStatusOptions(this.statusBaseline);
  }

  get skuStatusHint(): string {
    return statusRules.skuStatusHint(this.fg?.controls.skuStatus.value);
  }

  get skuStatusTone(): string {
    return statusRules.skuStatusTone(this.fg?.controls.skuStatus.value);
  }

  /** The pending archive intent (HUBX-8438). The flag AS READ is not kept beside it -
   *  that is origin.isArchived, and one copy of a fact is enough. Undo restores from
   *  there, which is what makes the intent revertible like any other form value. */
  isArchived = false;

  /** The state the form was built against — `target` on open, and the item as re-read once
   *  a conflict is resolved or discarded (HUBX-8466). The BASELINE the conflict comparison
   *  reads, and the source of every echo-only member.
   *
   *  A 412 does not move it: until the user resolves the conflict, the form is still the
   *  user's edit of what they read. */
  private origin?: ItemResponse;

  /** The members the other writer moved, keyed by request member (HUBX-8439). Each one
   *  puts a ring on its control; there is nothing else to them. Empty except between a
   *  412 and its resolution or Undo. */
  private conflicts = new Map<string, IConflictMark>();

  /** The two concurrency tokens the GET handed over (ADR-0005). Fields, not controls:
   *  nothing edits them.
   *
   *  They move with `origin`, and only with it. A 412 does not move them (HUBX-8466): Save
   *  stays blocked until the resolve popup has answered every member against the item as
   *  re-read, and only then do they become that item's. */
  private expectedRowVersion = '';
  private expectedItemRowVersion = '';

  /** What the server said this caller may write on this item (ADR-0011). Empty on
   *  Create, where the role rules in buildForm are the whole answer. */
  private writableFields: string[] = [];

  /** Permissions, every one of them server-stated (ADR-0011): the list says what THIS
   *  caller may write on THIS item, and nothing here infers one from a role. */
  private get aggregateWritable(): boolean {
    return writeAccess.isAggregateWritable(this.writableFields);
  }

  get mayArchive(): boolean {
    return writeAccess.mayArchive(this.writableFields);
  }

  get mayCurate(): boolean {
    return writeAccess.mayCurate(this.writableFields);
  }

  get inTodaysDeals(): boolean {
    return !!this.fg?.controls.todaysDeals.value;
  }

  /** ADMIN / VENDORMGR: the role the server lets approve. Read off the role, not
   *  writableFields — that list carries `status` for every role (ADR-0009 Rule 9), so it
   *  cannot tell an approver from a Seller. Drives notices only; the server decides. */
  get mayApprove(): boolean {
    return this.vendorService.isAdmin || this.vendorService.isVendorManager;
  }

  /** Nothing on this item is writable for this caller (HUBX-8493). The form is already
   *  locked by applyWritability and Save has nothing to carry; this is what says why. Read
   *  off the list alone, like every permission here — no role check. Edit only: on Create
   *  and Clone the list is empty by construction. */
  get readOnlyNotice(): boolean {
    return this.isEdit && writeAccess.isReadOnly(this.writableFields);
  }

  /** The two notices the collapsed Status control can raise: an operator approving a
   *  Pending item, and a Seller sending it back into review - by raising its cost, or by
   *  changing MOQ, MXQ, ETA, Special Notes or Restrictions on an approved item (HUBX-8515).
   *  Mutually exclusive by construction, since one needs mayApprove and the other its
   *  absence. Both exist because the user did not ask for the consequence in those words. */
  get approvalPending(): boolean {
    return statusRules.isApprovalPending(
      this.fg?.controls.skuStatus.value, this.statusBaseline, this.mayApprove);
  }

  get reviewPending(): boolean {
    return statusRules.isReviewPending(
      this.fg?.controls.cost.value, this.statusBaseline, this.mayApprove, this.fg?.getRawValue());
  }

  /** The third notice (HUBX-8476): a cost cut that brings a Removed item back. Edit only —
   *  Create and Clone have no stored status to reactivate from. Disjoint from reviewPending
   *  by construction: that one needs a cost above the stored one or an approved item, this
   *  one a Removed item and a cost at or below a threshold that is itself below the stored
   *  one. */
  get reactivationPending(): boolean {
    return this.isEdit && statusRules.isReactivationPending(
      this.fg?.controls.cost.value, this.statusBaseline, this.writableFields, this.reactivationThreshold);
  }

  /** The fourth notice (HUBX-8476, ADR-0009 Rule 9): an operator choosing Active on a Removed
   *  item whose cost does not reach the threshold - Save reactivates it manually. Never shows
   *  with reactivationPending: it holds only when that one does not. */
  get manualReactivationPending(): boolean {
    return this.isEdit && statusRules.isManualReactivationPending(
      this.fg?.controls.skuStatus.value, this.fg?.controls.cost.value, this.statusBaseline,
      this.writableFields, this.reactivationThreshold, this.mayApprove);
  }

  get manualReactivationNotice(): string {
    return copy.withStaysPaused(copy.manualReactivationNotice(this.reactivationThreshold), this.staysPaused);
  }

  /** Today's Deals (HUBX-8387). Like Archive, it writes nothing on its own: the next
   *  Save carries it and Undo takes it back. Goes through the control rather than a
   *  field so `fg.dirty` — and with it Undo's availability — stays true by itself.
   *
   *  The `mayCurate` guard is redundant with the button's own `disabled` and kept
   *  anyway: the permission decides what the REQUEST carries, so a caller that could
   *  somehow reach this method must not be able to set a value that then travels. */
  toggleTodaysDeals(): void {
    if (!this.mayCurate || this.archivedLock) {
      return;
    }
    this.fg.controls.todaysDeals.setValue(!this.inTodaysDeals);
    this.fg.controls.todaysDeals.markAsDirty();
  }

  /** Whether a text chip is inert. The chips are divs with a click handler, so a
   *  disabled control does not stop them on its own — both this and the guard in
   *  openTextModal are needed. */
  chipDisabled(field: 'restrictions' | 'specialNotes'): boolean {
    return this.fg.controls[field].disabled;
  }

  /** The chip's blue action: "Add", or "Edit/View" once it has text - and nothing on a
   *  locked chip, which offers nothing to click (the expanded listing view hides it too).
   *  The tooltip still shows the text. */
  chipAction(field: 'restrictions' | 'specialNotes'): string {
    if (this.chipDisabled(field)) { return ''; }
    return this.hasText(field) ? 'Edit/View' : 'Add';
  }

  get archiveIntentChanged(): boolean {
    return copy.archiveIntentChanged(this.isArchived, this.origin);
  }

  /** Header and primary-action copy. Clone keeps Create's wording on purpose: it
   *  creates an item, and the band already says what it was cloned from. */
  get modalTitle(): string {
    return this.isEdit ? 'Edit Item' : 'Create Item';
  }

  get submitLabel(): string {
    return this.isEdit ? 'Save' : 'Create Item';
  }

  /** Whether this Edit would carry anything — a form edit, or a pending archive intent,
   *  which is a change the form itself does not know about.
   *
   *  It measures INTERACTION, not state: a value seeded programmatically leaves the form
   *  pristine even when it differs from what the GET returned. That matters in one place —
   *  a legacy item whose category is seeded from the master (applyTarget) reads as
   *  unchanged, so materialising the per-vendor override needs the user to touch something.
   *  The alternative, diffing the built request against `origin` through `comparable`,
   *  measures state exactly but runs the whole request build on every change-detection
   *  pass; not worth it for that one case.
   *
   *  Anything that changes a control WITHOUT the user typing into it has to say so with
   *  markAsDirty — see toggleTodaysDeals, onTextModalSaved and applyPending. Both Undo and Save read
   *  this, so a change that forgets it is a change the user cannot save. */
  get hasPendingChanges(): boolean {
    return this.fg.dirty || this.archiveIntentChanged;
  }

  /** Undo is offered only when there is something to take back. */
  get canUndo(): boolean {
    return this.isEdit && this.hasPendingChanges;
  }

  get archiveIntentNotice(): string | null {
    return copy.archiveIntentNotice(this.isArchived, this.origin);
  }

  /** The target selector renders for operator roles as soon as the modal opens — it
   *  is NOT gated on the company list. The closed control only needs a label, and the
   *  label of the company we are already acting as is in the session; the list is
   *  needed solely to open the panel. Waiting for the fetch would leave a hole in the
   *  band for as long as the admin API takes.
   *
   *  Never on Edit: choosing a target company is a decision about where an item is
   *  BORN. An existing item already has a vendor and the PUT does not accept the
   *  member at all (ADR-0010), so there is no version of this control that means
   *  anything here. */
  get showTargetSelector(): boolean {
    return this.isPricingRole && !this.isEdit;
  }

  /** The provenance band renders when it has something to hold: a clone source on
   *  the left (HUBX-8422), a target selector on the right (ADR-0010), or both. On a
   *  plain Add Item for a SELLER it collapses entirely.
   *
   *  On Edit the same band holds the item header instead — one strip, three
   *  contents, so the modal never grows a second band for the same job. */
  get showBand(): boolean {
    return !!this.source || this.showTargetSelector || !!this.header;
  }

  /** Header dates, as absolute instants — same UTC correction as the clone source's,
   *  and for the same reason: the row carries them without an offset marker. */
  get headerLaunchedOn(): Date | null {
    return ItemWriteModalComponent.asInstant(this.header?.launchedOn);
  }

  get headerUpdatedOn(): Date | null {
    return ItemWriteModalComponent.asInstant(this.header?.updatedOn);
  }

  get dqControls(): UntypedFormArray {
    return this.fg.get('discountQtys') as UntypedFormArray;
  }

  get statusHint(): string {
    return this.fg.controls.status.value === 'Active'
      ? 'Goes live immediately'
      : 'Held for HUBX review';
  }

  get textModalTitle(): string {
    return this.textModalField === 'restrictions' ? 'Restrictions' : 'Special Notes';
  }

  get textModalValue(): string {
    return this.textModalField ? this.fg.controls[this.textModalField].value : '';
  }

  ngOnInit(): void {
    this.document.body.classList.add(ItemWriteModalComponent.OPEN_CLASS);
    this.destroyRef.onDestroy(() => this.document.body.classList.remove(ItemWriteModalComponent.OPEN_CLASS));
    this.buildForm();
    this.loadLookups();
    if (this.isPricingRole) {
      this.loadCompanies();
    }

    // A stale server error must not outlive the edit that could fix it.
    this.fg.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.serverError) {
          this.clearServerError();
        }
      });

    // Pre-submit category nudge (HUBX-8431): ask the server what item this MPN
    // already resolves to, and under which category. ONLY the MPN triggers a
    // call — a category change re-reads the stored answer (categoryNudge), never
    // the network. switchMap drops the in-flight answer for a superseded MPN, so
    // a fast typist can never be shown a nudge for what they used to have typed.
    // The Clone seed patches `mpn`, which emits here on purpose: a clone whose
    // category diverges from the shared item's should say so before submit, not
    // only after save (HUBX-8429).
    this.fg.controls.mpn.valueChanges
      .pipe(
        debounceTime(400),
        map((value: string) => (value ?? '').trim()),
        distinctUntilChanged(),
        // A blank MPN resolves to nothing and is a 400 at the endpoint, so it is
        // never sent. catchError sits INSIDE the inner observable: outside, one
        // failed lookup would complete the outer stream and kill the nudge for
        // the rest of the modal's life.
        switchMap(mpn => mpn
          ? this.itemCreateService.resolveByMpn(mpn).pipe(catchError(() => of(null)))
          : of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(resolution => this.mpnResolution = resolution);

    // Sold Out <-> Available, kept in step in BOTH directions - one fact behind two
    // controls. The rule is shared (rules/item-write-status.rules); what stays here is the
    // subscription, and the Create guard on the reverse direction: the status control is
    // blank on a Create so neither branch could fire anyway, but stating it keeps this
    // subscription's scope visible at the call site.
    this.fg.controls.skuStatus.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const available = this.fg.controls.availability;
        const next = statusRules.availabilityForStatus(value, available.value, this.statusBaseline);
        if (!next) {
          return;
        }
        available.setValue(next.availability, { emitEvent: false });
        if (next.markDirty) {
          available.markAsDirty();
        }
      });

    this.fg.controls.availability.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!this.isEdit) {
          return;
        }
        const status = this.fg.controls.skuStatus;
        const next = statusRules.statusForAvailability(value, status.value);
        if (next) {
          status.setValue(next, { emitEvent: false });
        }
      });
  }

  buildForm(): void {
    this.fg = createItemWriteForm(this.fb, this.isPricingRole);
  }

  /** Edit shows every refusal once there is a change to save (HUBX-8466), as the submit does:
   *  Save waits while an error is shown, so a defect the item was stored with, in a field no
   *  one touched, has to be ringed too, or Save would wait with nothing to say why. Keyed on
   *  hasPendingChanges - what Save and Undo read - so every way a change is made reveals, and
   *  going back to no change (Undo, or an archive toggled back) hides again. */
  ngDoCheck(): void {
    const reveal = !!this.fg && this.isEdit && this.hasPendingChanges;
    if (reveal && !this.revealed) {
      this.fg.markAllAsTouched();
    } else if (!reveal && this.revealed && !this.submitted) {
      this.fg.markAsUntouched();
    }
    this.revealed = reveal;
  }

  /** Save waits while the form shows an error, or while the server's refusal of this very edit
   *  stands (a 400, until a change) - HUBX-8466. A pristine Create shows none: its first Save
   *  reveals what is missing, and from then on waits until it is fixed. */
  get saveBlockedByError(): boolean {
    return this.serverRefused || validation.hasShownError(this.fg);
  }

  // The four validators and their helpers are shared now (rules/item-write-validators).
  // These wrappers are what keeps the template unchanged: it calls them by the same names,
  // with the same arguments, and never sees the form being passed through.
  showFieldError(name: string): boolean {
    return validation.showFieldError(this.fg, name);
  }

  showGroupError(key: string, ...triggers: string[]): boolean {
    return validation.showGroupError(this.fg, key, ...triggers);
  }

  /** The category pair's ring, on both selects - the predicate hasShownError reads too. */
  showCategoryError(): boolean {
    return validation.showCategoryError(this.fg);
  }

  /** The ring on a tier input: the input at fault (validation.dqFieldError), once the tier is
   *  engaged; a rule that compares tiers rings once any tier is (validation.dqInvalid). An
   *  incomplete pair rings the missing half only. */
  dqInvalid(index: number, field: 'fromQty' | 'price'): boolean {
    return validation.dqInvalid(this.dqControls, index, field);
  }

  dqError(index: number, key: string): boolean {
    return validation.dqError(this.dqControls, index, key);
  }

  /** As on the listing rows: a tier input left holding only its mask prefix ('<' or '$')
   *  is emptied on blur, so it does not read as a value (HUBX-8466). */
  checkPrefix(input: HTMLInputElement): void {
    if (input.value && input.value.length === 1) {
      input.value = '';
    }
  }

  /** The generic line comes with the submit, and whenever Save waits on a shown error
   *  (HUBX-8466): a blocked Save always says why. */
  get bannerError(): string | null {
    return copy.bannerError(this.fg, this.dqControls, this.serverError, this.submitted || validation.hasShownError(this.fg));
  }

  /** Pre-submit category nudge (HUBX-8431): the MPN already matches an existing
   *  item filed under a DIFFERENT category than the one selected here. Advisory
   *  only — category is a per-vendor facet (ADR-0007), so diverging is allowed and
   *  nothing blocks submit; the authoritative check is the write-ack warning after
   *  save (HUBX-8429), because a concurrent create can change the resolution
   *  between here and submit.
   *
   *  Silent in all four other cases: no answer yet / the lookup failed, the MPN
   *  resolves to nothing (a new item), the existing item carries no category
   *  (legacy — nothing to compare), or the categories match. Silent too when the
   *  id is not in the lookup tree: the message names the category, so without a
   *  name there is nothing to say. */
  get categoryNudge(): string | null {
    const resolution = this.mpnResolution;
    if (!resolution?.resolved) {
      return null;
    }
    const selected = this.fg.controls.categoryId.value;
    if (!selected || selected === resolution.categoryId) {
      return null;
    }
    return this.listedUnder(resolution.categoryId);
  }

  /** The shared sentence for both category advisories — the pre-submit nudge
   *  (HUBX-8431) and the post-save notice (HUBX-8429). One wording, one place: the
   *  two paths differ only in where the category id comes from (the resolution
   *  preview vs the write-ack warning), never in what they say.
   *
   *  Null when there is no id, or when the id is absent from the lookup tree — the
   *  sentence names the category, so with no name there is nothing to say. */
  private listedUnder(categoryId?: string | null): string | null {
    if (!categoryId) {
      return null;
    }
    const name = this.categoryPath(categoryId);
    return name ? `This part number is usually listed under ${name}.` : null;
  }

  /** Post-save category notice (HUBX-8429): the write-ack's
   *  CATEGORY_DIFFERS_FROM_MASTER advisory, resolved to the same sentence the
   *  pre-submit nudge shows. Built HERE rather than in the parent because the
   *  category name lives in this component's lookup tree; the parent only displays
   *  what it is handed. Unknown codes are ignored — the channel is generic and
   *  will grow codes this build has never heard of. */
  private categoryNoticeFrom(warnings: IWriteWarning[]): string | null {
    const warning = warnings.find(w => w.code === CATEGORY_DIFFERS_FROM_MASTER);
    return this.listedUnder(warning?.data?.['categoryId']);
  }

  /** Display path for a category id, walking the same one-level tree the selects
   *  use: 'Main / Sub' for a child, the plain name for a top-level node, null when
   *  the id is not in the tree. The walk is the shared one (editRules.categoryPair),
   *  which the listing rows draw their category pair from too (HUBX-8480). */
  private categoryPath(categoryId: string): string | null {
    const { main, sub } = editRules.categoryPair(this.categories, categoryId);
    if (!main) {
      return null;
    }
    return sub ? `${main.name} / ${sub.name}` : main.name;
  }

  loadLookups(): void {
    this.loader = true;
    this.lookupsError = false;
    forkJoin([
      this.itemCreateService.getManufacturers(),
      this.itemCreateService.getCategories(),
      this.itemCreateService.getAttributes(),
      this.itemCreateService.getExwPoints()
    ]).subscribe({
      next: ([manufacturers, categories, attributes, exwPoints]) => {
        this.manufacturers = manufacturers;
        this.categories = categories;
        this.condition = attributes.find(a => a.label === 'Condition');
        this.packaging = attributes.find(a => a.label === 'Packaging');
        this.warranty = attributes.find(a => a.label === 'Warranty');
        this.exwPoints = exwPoints;
        if (this.seed) {
          this.applySeed(this.seed);
        }
        if (this.target) {
          this.applyTarget(this.target);
          if (this.pending) {
            this.applyPending(this.pending);
            if (this.refusal) {
              this.applyRefusal(this.refusal);
            }
          }
        }
        this.applyDefaultExw();
        this.loader = false;
      },
      error: () => {
        this.loader = false;
        this.lookupsError = true;
      }
    });
  }

  /** The ExW point a blank Create defaults to (HUBX-8447), matched by description. */
  private static readonly defaultExwDescription = 'Miami';

  /** Default ExW on a blank Create (HUBX-8447): preselect Miami so the vendor does not
   *  have to state the overwhelmingly common origin on every item.
   *
   *  Resolved by matching the loaded lookup's DESCRIPTION, never by a hardcoded id:
   *  dbo.ExwPoints.Id is an int assigned per environment, so an id that means Miami in
   *  dev means something else in production. Full equality, never a substring — 'Miami'
   *  would also match 'Miami Beach' or 'North Miami', and the list is ordered by
   *  description, so a substring match would silently default to whichever of those
   *  sorts first.
   *
   *  Compared trimmed and case-insensitively because dbo.ExwPoints.Description is
   *  unconstrained nvarchar; a stray space in one environment's data would otherwise
   *  make the default silently not apply, indistinguishable from Miami being absent.
   *  The `?? ''` is load-bearing rather than decorative: the column is NULLABLE and
   *  ExwPointReader does not filter nulls, so a bare .trim() on a null description
   *  would throw HERE, inside the forkJoin next handler — leaving `loader` true and
   *  `lookupsError` false, i.e. the modal stuck on the spinner with nothing to say.
   *
   *  Two guards, for two different reasons. `isEdit` first: a default belongs to a NEW
   *  item, and on Edit the stored answer is the item's own — including a stored empty
   *  one, which this must not quietly fill in and then write back as a real change on
   *  the next Save. (HUBX-8447 was written when Create and Edit were separate
   *  components and only needed the second guard; this modal now serves both, so the
   *  mode is stated rather than inferred.) Then the empty check, which is what keeps
   *  Clone intact: a seeded ExW is the source item's real answer, and the default must
   *  never overwrite it.
   *
   *  No Miami in the lookup — deactivated per ADR-0012 / HUBX-8446, or an environment
   *  without one — leaves the control empty and required, and the vendor picks exactly
   *  as before this change. Doing nothing IS the contingency: no error, no banner, no
   *  crash.
   *
   *  Set as a string: the select binds its option values with [value] (stringified), the
   *  same coercion applySeed makes with String(s.exwId); buildRequest converts back with
   *  Number(). setValue leaves the control untouched/pristine, so the preselection shows
   *  no red ring.
   */
  private applyDefaultExw(): void {
    if (this.isEdit || this.fg.controls.exwId.value) {
      return;
    }
    const target = ItemWriteModalComponent.defaultExwDescription.toLowerCase();
    const miami = this.exwPoints.find(e => (e.description ?? '').trim().toLowerCase() === target);
    if (miami) {
      this.fg.controls.exwId.setValue(String(miami.id));
    }
  }

  /** Target company list (ADR-0010). Deliberately NOT folded into the lookups
   *  forkJoin: a failure there sets `lookupsError` and blocks submit, but failing to
   *  load the company list must not stop an operator creating for the company it is
   *  already in — on error the list stays empty, the control does not render, and the
   *  create proceeds against the ambient vendor exactly as before this feature.
   *
   *  Fetched per modal open rather than cached. The portfolio is authorization-shaped
   *  data: §7 holds that nothing on the target-authorization path is cached, and a
   *  stale list would offer a company the server will refuse. */
  private loadCompanies(): void {
    this.vendorService.getAllCustomers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          // Alphabetical by company name so the picker is scannable; the server
          // returns portfolio order, which is arbitrary to the operator. Copy
          // before sorting rather than mutating the service's array, and keep it
          // case-insensitive/locale-aware so "Ábaco" and "abaco" sit together.
          this.companies = [...(result?.customers ?? [])].sort((a, b) =>
            (a.companyName ?? '').localeCompare(b.companyName ?? '', undefined, { sensitivity: 'base' }));
          this.ambientVendorId = this.resolveAmbientCompany(this.companies)?.vendorId ?? null;
          // Preselect the company already being acted as, so the selector shows the
          // operator's current context by default. The value is sent as-is and the
          // server collapses it to a no-op when it equals the authoritative context
          // (buildRequest), so an untouched selector creates under the current company
          // without the client deciding that on its own.
          if (this.ambientVendorId) {
            this.fg.controls.targetVendorId.setValue(this.ambientVendorId);
          }
        },
        error: () => {
          this.companies = [];
        }
      });
  }

  /** Company picker UI state. Both are presentation only — the chosen company lives
   *  in the `targetVendorId` form control, never here, so there is one source of
   *  truth for what gets sent. */
  companyPickerOpen = false;
  companyFilter = '';

  /** Label for the closed control. Two sources, in order: once the list is in, the
   *  name of whatever `targetVendorId` holds — always consistent with what will be
   *  sent. Before that, the company name the session already knows, so the control
   *  shows the right answer from the first frame instead of a placeholder that
   *  flickers into the real value a moment later. */
  get selectedCompanyName(): string {
    const id = this.fg?.controls.targetVendorId.value;
    const fromList = this.companies.find(c => c.vendorId === id)?.companyName;
    return fromList ?? sessionStorage.getItem('company_name') ?? '';
  }

  /** Case-insensitive contains over the list already in memory. The whole portfolio
   *  arrives in one payload, so filtering costs nothing and fetches nothing. */
  get filteredCompanies(): IVendorCompany[] {
    const term = this.companyFilter.trim().toLowerCase();
    return term
      ? this.companies.filter(c => (c.companyName ?? '').toLowerCase().includes(term))
      : this.companies;
  }

  toggleCompanyPicker(): void {
    // Nothing to show until the list lands; the button is disabled meanwhile, and
    // this guard keeps that true even if something else calls in.
    if (!this.companies.length) {
      return;
    }
    this.companyPickerOpen = !this.companyPickerOpen;
    // Clear on every open/close: a term left over from last time would hide most of
    // the list the next time the panel appears, with no visible reason why.
    this.companyFilter = '';
  }

  selectCompany(company: IVendorCompany): void {
    this.fg.controls.targetVendorId.setValue(company.vendorId);
    this.companyPickerOpen = false;
    this.companyFilter = '';
  }

  /** Which company this session is acting as. Settled once when the session was minted
   *  (ImpersonationService) and recorded there, so the switcher and this selector can
   *  never name different companies — both just read it.
   *
   *  In the vendor portal there is always an impersonated company (true as of
   *  2026-07-28, but not enforced anywhere here), so in practice the code is present
   *  and found. When it is present but NOT in the list, this resolves to nothing,
   *  leaving the selector unseeded (blank). buildRequest then sends no target and the
   *  server creates under its authoritative context — the safe outcome. Substituting
   *  some other company would preselect one the operator may not actually be acting
   *  as, so it is deliberately avoided. */
  private resolveAmbientCompany(companies: IVendorCompany[]): IVendorCompany | undefined {
    return companies.find(c => c.bpCode === ImpersonationService.selectedCompanyCode);
  }

  /** Clone source dates, as absolute instants (HUBX-8422).
   *
   *  The API sends these as UTC with NO offset marker — '2026-07-06T05:42:00', the
   *  shape generic-countdown documents in its own sample. ECMAScript parses a
   *  bare date-time as LOCAL, so handing the raw string to DatePipe renders the UTC
   *  clock as though it were the user's own: off by the whole offset, and silently,
   *  because the result is a plausible time. Marking the string as UTC lets the
   *  runtime convert properly, which is the same correction generic-countdown and
   *  offer.service make with `moment.utc(...).tz(...)`. */
  get launchedOnLocal(): Date | null {
    return ItemWriteModalComponent.asInstant(this.source?.launchedOn);
  }

  get updatedOnLocal(): Date | null {
    return ItemWriteModalComponent.asInstant(this.source?.updatedOn);
  }

  private static asInstant(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    // A Date is already an absolute instant, and a string that states its own
    // offset needs no help — only the bare form is ambiguous, so only it is fixed.
    if (value instanceof Date) {
      return value;
    }
    const statesOffset = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value);
    return new Date(statesOffset ? value : `${value}Z`);
  }

  /** Clone seeding (§4.3): fill the form from an existing item so the new item
   *  starts as a copy. What is deliberately NOT copied stays at its build-form
   *  default, which IS the §4.3 reset: VendorItemCode and Availability blank
   *  (both required — the user supplies new ones), HubxItemCode blank (re-defaults
   *  server-side), Status = Pending, and the commercial terms — Cost, Price and
   *  every DQ tier — left empty (2026-07-28, below). Tags and any attributes beyond
   *  the three the modal models are out of scope — Clone reuses the Create form
   *  as-is (no extra controls, no passthrough). Runs after lookups load. setValue
   *  here does not mark controls touched/dirty, so nothing shows a red ring until
   *  the user engages a field. */
  private applySeed(s: ItemResponse): void {
    // Copied: what DESCRIBES the product. Money is not copied — see below.
    this.fg.patchValue({
      mpn: s.mpn ?? '',
      description: s.description ?? '',
      manufacturerId: s.manufacturerId ?? '',
      leadTimeDays: s.leadTimeDays ?? null,
      moq: s.minimumOrderQty,
      mxq: s.maximumOrderQty ?? null,
      restrictions: s.restrictions ?? '',
      specialNotes: s.specialNotes ?? ''
    });

    // EXW select binds option values with [value] (strings), so the model value
    // must be the string form to match an option; buildRequest coerces back with
    // Number().
    this.fg.controls.exwId.setValue(String(s.exwId));

    this.seedCategory(s.categoryId ?? s.masterCategoryId);
    this.seedAttributes(s.attributes);

    // Cost, Price and the DQ tiers are deliberately NOT seeded (2026-07-28). The
    // dividing line for a clone is: copy what describes the product, reset what
    // prices it. Commercial terms are the facet most likely to be wrong when
    // copied — and the target-company selector makes that sharper, since a clone
    // into another company carries a different cost basis and margin entirely, so
    // inherited numbers would be wrong by construction rather than merely stale.
    // Both are required at Create (Cost for every role, Price for operators), so
    // the form makes the user state them instead of letting stale money through
    // unnoticed.
  }

  /** Edit seeding (§4.2, HUBX-8388): FULL STATE. Every member the body carries is
   *  filled from what the GET returned, money included — the opposite of applySeed.
   *  The contract is that the response re-submitted verbatim produces an empty
   *  delta, so anything left at a build-form default here would read as a real
   *  change the user never made.
   *
   *  Two members are held outside the form: the archived flag, which the Archive action
   *  owns rather than a control, and the two concurrency tokens, which nothing edits. The
   *  status axes are NOT among them any more - they are read off `origin` when the request
   *  is built, because that is where they already are.
   *
   *  What a Seller does NOT receive (unitPrice, discountQtys — §8.3 withholds them)
   *  simply stays null/empty, and buildEditRequest omits it, which is exactly what
   *  PricingPolicy expects from that role. */
  private applyTarget(t: ItemResponse): void {
    const subCategories = editRules.seedEditForm(this.fg, t, { ...this.attributeLookups, categories: this.categories });
    if (subCategories) {
      this.subCategories = subCategories;
    }

    this.isArchived = t.isArchived;
    this.expectedRowVersion = t.rowVersion;
    this.expectedItemRowVersion = t.itemRowVersion;
    this.writableFields = t.writableFields ?? [];
    // The baseline the conflict comparison reads (HUBX-8439). Set here and reassigned
    // only by a 412 — one place per state the form was built against.
    this.origin = t;
    this.applyWritability();
  }

  /** The listing row's unsaved edit over the stored target (see `pending`). Only the form
   *  and the archive intent move; `origin`, the tokens and writableFields stay the target's,
   *  so writability is applied again after the seeding patched the controls. */
  private applyPending(p: ItemResponse): void {
    const subCategories = editRules.seedEditForm(this.fg, p, { ...this.attributeLookups, categories: this.categories });
    if (subCategories) {
      this.subCategories = subCategories;
    }
    this.isArchived = p.isArchived;
    this.applyWritability();
    // The row's edit differs from `origin` and is not saved, but seeding by patchValue leaves
    // the form pristine: say so, or Save and Undo would not be offered (HUBX-8480).
    this.fg.markAsDirty();
  }

  /** Narrow the form to what the server granted on THIS item (ADR-0011). Edit only: on
   *  Create the role rules the factory already applied are the whole answer.
   *
   *  An archived item is then locked whole (HUBX-8466), as its listing row is: the shared
   *  rule sets every control, so running it again is also how Restore unlocks to exactly
   *  what the server grants. */
  private applyWritability(): void {
    writeAccess.applyWritability(this.fg, this.writableFields);
    if (this.archivedLock) {
      this.fg.disable({ emitEvent: false });
    }
  }

  /** An archived item is inert until Restore (HUBX-8466), the rule its listing row already
   *  follows. Edit only: Create and Clone have nothing archived. Save stays live, so the
   *  archive intent itself can be saved; the request is built from getRawValue(). */
  get archivedLock(): boolean {
    return this.isEdit && this.isArchived;
  }

  // ---------------------------------------------------------------------------
  // Write conflict (412) - HUBX-8439
  // ---------------------------------------------------------------------------

  /** The three attribute lookups the comparison resolves ids through. Undefined while
   *  /lookups/attributes is in flight, and then both sides compare as null - equal, so
   *  unmarked, which is the right answer when nothing is known yet. */
  private get attributeLookups(): conflictRules.IAttributeLookups {
    return { condition: this.condition, warranty: this.warranty, packaging: this.packaging };
  }

  /** Ring what a 412 says the other writer moved (HUBX-8439). The comparison and the
   *  marking rule are shared.
   *
   *  Nothing else moves (HUBX-8466): the baseline, the two tokens, the permissions and the
   *  form stay as they were, and Save stays blocked until the user resolves the conflict in
   *  the resolve popup, against the item as re-read. A 412 that rebased the tokens let the
   *  next Save overwrite members the user never looked at. */
  private applyConflict(current: ItemResponse): void {
    const lookups = this.attributeLookups;
    const before = conflictRules.comparable(this.origin ?? {}, this.origin?.effectiveStatus ?? '', lookups);
    const after = conflictRules.comparable(current, current.effectiveStatus, lookups);
    const mine = conflictRules.comparable(
      { ...this.buildEditRequest(), isArchived: this.isArchived },
      this.fg.getRawValue().skuStatus,
      lookups);
    this.conflicts = conflictRules.diffConflicts(before, after, mine);
  }

  /** Put the stored value into the form for one member: the members the resolve popup
   *  answered Current, a member this caller may not write among them. */
  private takeTheirs(key: string, current: ItemResponse): void {
    switch (key) {
      case 'categoryId':
        this.fg.controls.categoryMainId.setValue('', { emitEvent: false });
        this.subCategories = [];
        // The effective category, as seeding shows it (ADR-0007).
        this.seedCategory(current.categoryId ?? current.masterCategoryId);
        return;
      case 'exwId':
        this.fg.controls.exwId.setValue(String(current.exwId));
        return;
      case 'effectiveStatus':
        this.fg.controls.skuStatus.setValue(current.effectiveStatus, { emitEvent: false });
        return;
      case 'isArchived':
        this.isArchived = current.isArchived;
        this.applyWritability();
        return;
      case 'unitPurchasePrice':
        this.fg.controls.cost.setValue(current.unitPurchasePrice ?? null);
        return;
      case 'minimumOrderQty':
        this.fg.controls.moq.setValue(current.minimumOrderQty ?? null);
        return;
      case 'maximumOrderQty':
        this.fg.controls.mxq.setValue(current.maximumOrderQty ?? null);
        return;
      case 'attributes.Condition':
        editRules.seedAttribute(this.fg, 'condition', this.condition, current.attributes);
        return;
      case 'attributes.Warranty':
        editRules.seedAttribute(this.fg, 'warranty', this.warranty, current.attributes);
        return;
      case 'attributes.Packaging':
        editRules.seedAttribute(this.fg, 'packaging', this.packaging, current.attributes);
        return;
      case 'discountQtys':
        // Whole-collection, because that is the granularity the store speaks: the tiers
        // are one member of the body, not three independent ones.
        this.dqControls.controls.forEach(g => g.patchValue({ fromQty: null, price: null }));
        (current.discountQtys ?? []).slice(0, 3).forEach((tier, i) =>
          this.dqControls.at(i).patchValue({ fromQty: tier.fromQty, price: tier.price }));
        return;
      case 'tags':
        // No control and no intent — the body echoes `origin`'s, so moving `origin` to the
        // item as re-read (done by the caller) is the whole of it. The popup never lists it.
        return;
      default:
        this.fg.controls[key]?.setValue((current as { [k: string]: any })[key] ?? '');
    }
  }

  /** The mark on one control, or null. The template looks it up by CONTROL name, since
   *  that is what it has in hand. */
  conflictFor(control: string): IConflictMark | null {
    return conflictRules.conflictFor(this.conflicts, control);
  }

  /** Whether a control carries a mark - what the ring keys off, and the whole of what a
   *  mark does. */
  conflictOpen(control: string): boolean {
    return !!this.conflictFor(control);
  }

  get hasConflict(): boolean {
    return this.conflictSeen;
  }

  /** Whether a 412 has come back and not yet been cleared. Set even when the payload
   *  carried no `currentState` — the tokens are stale either way, and that is the case
   *  where the user most needs the way out. */
  private conflictSeen = false;

  get conflictNotice(): string | null {
    return this.conflictSeen ? copy.CONFLICT_NOTICE : null;
  }

  readonly reviewLabel = copy.REVIEW_CHANGES;

  /** The resolve popup (HUBX-8466; design "Listing — shared write rules", Block B): open,
   *  its rows once the item is re-read (null while it is), a failed re-read, and the item the
   *  re-read returned - the new baseline when the popup is answered. */
  resolveOpen = false;
  resolveRows: resolveRules.IResolveRow[] | null = null;
  resolveError: string | null = null;
  private resolveFresh?: ItemResponse;

  /** Review changes on the band: the popup opens and the item is re-read for it. A real
   *  re-read rather than the 412's `currentState`: the payload is absent on the conflict the
   *  server could not re-read, and the item may have moved again since. */
  reviewConflict(): void {
    if (!this.target || !this.conflictSeen) {
      return;
    }
    this.resolveOpen = true;
    this.readResolve();
  }

  /** The re-read behind the popup, and its Try again: every member where the form differs
   *  from the item as re-read, whether or not the user edited it. */
  readResolve(): void {
    if (!this.target || !this.origin) {
      return;
    }
    this.resolveRows = null;
    this.resolveError = null;
    this.resolveFresh = undefined;
    this.itemCreateService.getItem(this.target.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: fresh => {
          if (!this.resolveOpen || !this.origin) {
            return;
          }
          this.resolveFresh = fresh;
          this.resolveRows = resolveRules.resolveRows(
            resolveRules.itemSide(this.origin),
            resolveRules.itemSide(fresh),
            resolveRules.itemSide(this.formItem()),
            fresh.writableFields ?? [],
            this.resolveLookups);
        },
        error: () => {
          if (this.resolveOpen) {
            this.resolveError = copy.RELOAD_FAILED;
          }
        }
      });
  }

  /** The popup answered. The item as re-read becomes the baseline - its tokens and
   *  writableFields with it - and the form keeps the members kept as Yours and takes the
   *  rest from it. Nothing is saved: Save does that, as on any edit.
   *
   *  Nothing kept as Yours is Discard my changes, and then the form is the item as re-read, with
   *  nothing left to save - and, as Undo, the listing row's edit of the item goes too. */
  onResolved(choices: Map<string, resolveRules.ResolveChoice>): void {
    const fresh = this.resolveFresh;
    const rows = this.resolveRows;
    if (!fresh || !rows) {
      return;
    }
    const current = rows.filter(row => !row.writable || choices.get(row.key) !== 'yours');
    if (current.length === rows.length) {
      // Discard my changes is Undo against the item as re-read: the listing row's edit of this item
      // goes too, as with Undo.
      this.takeFresh(fresh);
      if (this.target) {
        this.undone.emit(this.target.id);
      }
    } else {
      this.origin = fresh;
      this.expectedRowVersion = fresh.rowVersion;
      this.expectedItemRowVersion = fresh.itemRowVersion;
      this.writableFields = fresh.writableFields ?? [];
      this.applyWritability();
      for (const row of current) {
        this.takeTheirs(row.key, fresh);
      }
      this.clearConflicts();
      this.fg.markAsDirty();
    }
    this.closeResolve();
  }

  /** Cancel or the X: the conflict stays open. */
  closeResolve(): void {
    this.resolveOpen = false;
    this.resolveRows = null;
    this.resolveError = null;
    this.resolveFresh = undefined;
  }

  /** The form as the item it would save, for the popup's Yours: the same representation as
   *  the listing row's edit (editedItemFromForm), so both surfaces list the same rows - the
   *  category kept as the raw override when the user did not move it, for one. */
  private formItem(): ItemResponse {
    const item = editRules.editedItemFromForm(this.fg.getRawValue(), this.origin!, this.attributeLookups);
    return { ...item, isArchived: this.isArchived };
  }

  private get resolveLookups(): resolveRules.IResolveLookups {
    return { ...this.attributeLookups, manufacturers: this.manufacturers, categories: this.categories, exwPoints: this.exwPoints };
  }

  /** The form becomes `fresh`, untouched: Discard my changes, and Undo on a conflict. */
  private takeFresh(fresh: ItemResponse): void {
    this.fg.reset(undefined, { emitEvent: false });
    this.applyTarget(fresh);
    this.clearConflicts();
    this.submitted = false;
    this.clearServerError();
    this.fg.markAsPristine();
    this.fg.markAsUntouched();
  }

  private clearConflicts(): void {
    this.conflicts.clear();
    this.conflictSeen = false;
  }

  /** Resolve the effective categoryId (leaf) back to the main + optional sub the
   *  two selects expect, walking the same one-level tree onMainCategoryChange uses.
   *  Unresolvable ids (e.g. a legacy category no longer in the lookup tree) leave
   *  the selects blank so the required validator makes the user pick. */
  private seedCategory(categoryId?: string | null): void {
    const subCategories = editRules.seedCategory(this.fg, this.categories, categoryId);
    if (subCategories) {
      this.subCategories = subCategories;
    }
  }

  /** Seed the three modelled attributes (shared rule, rules/item-write-edit.rules).
   *  Attributes outside the three are not modelled by the form; on Edit they travel by
   *  echo, and a Clone leaves them out. */
  private seedAttributes(attributes: ICreateItemAttribute[]): void {
    editRules.seedAttributes(this.fg, this.attributeLookups, attributes);
  }

  /** Main category picked: expose its children (if any) and resolve categoryId
   *  to the main node when it is already a leaf. The rule is shared with the listing rows
   *  (editRules.categoryForMainChoice, HUBX-8480), so the two cannot answer differently. */
  onMainCategoryChange(): void {
    const choice = editRules.categoryForMainChoice(this.categories, this.fg.controls.categoryMainId.value);
    this.subCategories = choice.subCategories;
    this.fg.controls.categoryId.setValue(choice.categoryId);
  }

  onSubCategoryChange(): void {
    // categoryId is bound directly to the sub select; nothing else to derive.
  }

  hasText(field: 'restrictions' | 'specialNotes'): boolean {
    const value = this.fg.controls[field].value;
    return !!value && !!value.trim();
  }

  openTextModal(field: 'restrictions' | 'specialNotes'): void {
    // The chip is a div, so `disabled` on the control stops nothing by itself. This
    // guard is what actually holds: without it, a read-only item's notes could still
    // be rewritten through the text modal, which calls setValue on the way back.
    if (this.chipDisabled(field)) {
      return;
    }
    this.textModalField = field;
  }

  onTextModalSaved(text: string): void {
    if (this.textModalField) {
      // markAsDirty because setValue does not: the text modal is the only way to edit
      // these two, so without it a note the user just wrote leaves the form pristine —
      // Undo would not be offered and Save would stay disabled (hasPendingChanges).
      this.fg.controls[this.textModalField].setValue(text);
      this.fg.controls[this.textModalField].markAsDirty();
    }
    this.textModalField = null;
  }

  onTextModalClosed(): void {
    this.textModalField = null;
  }

  close(): void {
    this.closed.emit();
  }

  /** Escape closes the innermost open thing: the resolve popup (its Cancel), else the text
   *  editor, else the company picker, else the modal itself - as the X does, which asks nothing either. Not while a save is in
   *  flight: the answer still has to land somewhere. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.loader) { return; }
    if (this.resolveOpen) {
      this.closeResolve();
      return;
    }
    if (this.textModalField) {
      this.onTextModalClosed();
      return;
    }
    if (this.companyPickerOpen) {
      this.companyPickerOpen = false;
      return;
    }
    this.close();
  }

  submit(): void {
    // A conflict is resolved before anything is saved (HUBX-8466); the button says so.
    if (this.hasConflict) {
      return;
    }
    this.submitted = true;
    this.clearServerError();
    if (this.fg.invalid) {
      // reveal every offending field at once (rings) and surface the banner
      this.fg.markAllAsTouched();
      return;
    }

    this.loader = true;
    if (this.isEdit) {
      this.checkOffersThenSave();
      return;
    }

    this.itemCreateService.createItem(this.buildRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.loader = false;
          // parent (items.component) closes the modal, reloads the list, and shows
          // the confirmation. The code comes from the form — the ack does carry it,
          // but the form value is exactly what was just submitted. The category
          // notice (HUBX-8429) is resolved to a sentence HERE, while the lookup tree
          // is still in hand: once the modal closes there is no category control to
          // sit beside, so the advisory rides the confirmation instead.
          this.created.emit({
            id: result.id,
            vendorItemCode: (this.fg.value.vendorItemCode ?? '').trim(),
            categoryNotice: this.categoryNoticeFrom(result.warnings) ?? undefined
          });
        },
        error: (err: HttpErrorResponse) => {
          this.loader = false;
          this.applyServerError(err);
        }
      });
  }

  /** Map the form to the API contract (ICreateItemRequest): optional members are
   *  OMITTED when empty (never null), attributes carry {attributeId, value:code},
   *  createApproved is only sent for ADMIN/VM, and SELLER-disabled controls
   *  (UnitPrice/XID/DQ/Status) drop out of fg.value on their own. */
  private buildRequest(): ICreateItemRequest {
    const v = this.fg.value as { [key: string]: any };

    const attributes: ICreateItemAttribute[] = [];
    if (this.condition && v.condition) { attributes.push({ attributeId: this.condition.id, value: v.condition }); }
    if (this.warranty && v.warranty) { attributes.push({ attributeId: this.warranty.id, value: v.warranty }); }
    if (this.packaging && v.packaging) { attributes.push({ attributeId: this.packaging.id, value: v.packaging }); }

    const request: ICreateItemRequest = {
      vendorItemCode: (v.vendorItemCode ?? '').trim(),
      mpn: (v.mpn ?? '').trim(),
      description: (v.description ?? '').trim(),
      manufacturerId: v.manufacturerId,
      categoryId: v.categoryId,
      availability: Number(v.availability),
      leadTimeDays: Number(v.leadTimeDays),
      exwId: Number(v.exwId),
      attributes
    };

    const xid = (v.hubxItemCode ?? '').trim();
    if (xid) { request.hubxItemCode = xid; }
    if (v.cost != null && v.cost !== '') { request.unitPurchasePrice = Number(v.cost); }
    if (v.unitPrice != null && v.unitPrice !== '') { request.unitPrice = Number(v.unitPrice); }
    if (v.moq != null && v.moq !== '') { request.minimumOrderQty = Number(v.moq); }
    if (v.mxq != null && v.mxq !== '') { request.maximumOrderQty = Number(v.mxq); }
    if (this.isPricingRole) { request.createApproved = v.status === 'Active'; }

    // Target company (ADR-0010): send the selection as-is. Whether it counts as a
    // real retarget is decided server-side, where the caller's vendor context is
    // authoritative — a target equal to that context is collapsed to "no retarget"
    // there (ItemsController), and only a DIFFERING value runs the target-vendor
    // policy. The client does NOT pre-null against its own notion of the ambient
    // vendor: that notion comes from sessionStorage and can lag the real context, so
    // omitting on a client-side match risks silently creating under a company other
    // than the one the selector shows. For a SELLER the control is disabled, so
    // `v.targetVendorId` is undefined here and nothing is sent.
    if (v.targetVendorId) {
      request.targetVendorId = v.targetVendorId;
    }

    const specialNotes = (v.specialNotes ?? '').trim();
    if (specialNotes) { request.specialNotes = specialNotes; }
    const restrictions = (v.restrictions ?? '').trim();
    if (restrictions) { request.restrictions = restrictions; }

    const discountQtys: ICreateItemDiscountQty[] = (v.discountQtys ?? [])
      .filter((d: any) => d && d.fromQty != null && d.fromQty !== '' && d.price != null && d.price !== '')
      .map((d: any) => ({ fromQty: Number(d.fromQty), price: Number(d.price) }));
    if (discountQtys.length) { request.discountQtys = discountQtys; }

    return request;
  }

  /** Archive / restore (HUBX-8438). Writes NOTHING on its own — it flips the pending
   *  intent and the next Save carries it, which is what makes Undo able to take it
   *  back and what lets one Save both correct an item and take it off the shelf.
   *
   *  The form is locked while the item is archived, as legacy and the listing rows lock it
   *  (HUBX-8466): Archive puts it to sleep, Restore wakes it. Restore-and-correct is still
   *  one save - Restore, edit, Save. */
  toggleArchived(): void {
    this.isArchived = !this.isArchived;
    this.applyWritability();
    this.clearServerError();
  }

  /** Whether the last failure was the XID lock (HUBX-8458). Drives the Revert link beside
   *  the banner, and nothing else - the ring and the message come from the shared error
   *  catalog like every other code.
   *
   *  It exists because this 409 is the only one in the table with a WAY OUT that the form
   *  cannot express on its own. Every other terminal code names a value the user chose and
   *  can retype; here the value they need back is the one they overwrote, and after a failed
   *  Save it is nowhere on screen. Undo would bring it back, but it discards the rest of the
   *  form — too blunt for a single field. */
  xidLockedInB1 = false;

  /** The stored XID this Revert would restore, or null when the item has none — a legitimate
   *  state (A-R10 defaults a blank XID server-side), and then there is no value to name, so
   *  the link says only "Revert".
   *
   *  From `origin`, like every other echo-only member: after a resolved conflict that is the
   *  item as re-read, and reverting to the dead pre-conflict value would re-open the conflict
   *  this modal just resolved. */
  get storedXid(): string | null {
    const stored = (this.origin?.hubxItemCode ?? '').trim();
    return stored ? stored : null;
  }

  /** Put the stored XID back (HUBX-8458). Only the one field: the 409 says nothing about
   *  the rest of the form, and discarding a whole edit to undo one value would be a worse
   *  trade than the one Undo already offers deliberately.
   *
   *  Clears the control's server ring itself — the banner goes with `clearServerError`, but
   *  `setErrors({server:true})` was set by hand and updateValueAndValidity is what takes it
   *  off, since the control's own validators never produced it. */
  revertXid(): void {
    const control = this.fg.get('hubxItemCode');
    control?.setValue(this.origin?.hubxItemCode ?? '');
    control?.setErrors(null);
    control?.updateValueAndValidity();
    this.clearServerError();
  }

  /** Drop the banner and everything that hangs off it. One place, because the flag and the
   *  message are two halves of one state: a Revert link outliving its message would offer a
   *  way out of a failure the user can no longer see. */
  private clearServerError(): void {
    this.serverError = null;
    this.serverRefused = false;
    this.xidLockedInB1 = false;
  }

  /** Undo (HUBX-8388): put the form back to the state the GET returned. A local
   *  reset, not a re-read — the modal has written nothing, so there is nothing on
   *  the server to go back to. (The legacy popup re-fetches because it mutates the
   *  grid's row object in place and queues it in a batch; this one does not.)
   *
   *  Everything is restored: the form, the archived intent, the touched/submitted
   *  state that drives the rings, and any server error left over from a failed try.
   *  Then the page is told (`undone`), and the listing row's edit of this item goes too
   *  (HUBX-8480). */
  undo(): void {
    if (!this.target) {
      return;
    }
    const id = this.target.id;
    // On an open conflict the baseline is the state the other writer has already moved on
    // from, so Undo is Discard my changes (HUBX-8466): the item is re-read and the form takes it.
    // A failed re-read discards nothing and leaves the conflict open, to try again.
    if (this.conflictSeen) {
      this.loader = true;
      this.itemCreateService.getItem(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: fresh => {
            this.loader = false;
            this.takeFresh(fresh);
            this.undone.emit(id);
          },
          error: () => {
            this.loader = false;
            this.serverError = copy.RELOAD_FAILED;
          }
        });
      return;
    }
    // Against `origin`, not `target`: after a resolved conflict the state the GET returned
    // is dead - its tokens already fail - so Undo lands on the item as re-read then
    // (HUBX-8466). Before any conflict the two are the same object.
    this.takeFresh(this.origin ?? this.target);
    this.undone.emit(id);
  }

  /** Before the PUT (HUBX-8511): ask which pending offers this save would cancel, with the
   *  body about to be PUT. None (204) saves as before. Some (200) go to the page's warning
   *  and the modal waits, loader on — which also leaves Escape to the warning. A failed
   *  check lands in the banner, as a failed PUT does, and nothing is saved. */
  private checkOffersThenSave(): void {
    this.itemCreateService.affectedOffers(this.target!.id, this.buildEditRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: affected => affected ? this.offersAffected.emit(affected) : this.save(),
        error: (err: HttpErrorResponse) => {
          this.loader = false;
          this.applyServerError(err);
        }
      });
  }

  /** The page's warning answered Cancel (HUBX-8511): the PUT goes as it would have. */
  continueSave(): void {
    this.save();
  }

  /** The page's warning answered Keep, or was closed (HUBX-8511): nothing is saved, and the
   *  modal stays open with the edit. */
  keepEdit(): void {
    this.loader = false;
  }

  private save(): void {
    this.itemCreateService.editItem(this.target!.id, this.buildEditRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.loader = false;
          this.saved.emit({
            id: result.id,
            vendorItemCode: (this.fg.getRawValue().vendorItemCode ?? '').trim(),
            categoryNotice: this.categoryNoticeFrom(result.warnings) ?? undefined,
            // Approved or reactivated, and still Paused by the vendor (HUBX-8466).
            statusNotice: this.staysPaused ? copy.savedStaysPausedNote(this.statusBaseline!.businessStatus) : undefined
          });
        },
        error: (err: HttpErrorResponse) => {
          this.loader = false;
          this.applyServerError(err);
        }
      });
  }

  /** Map the form to the Edit contract. The rule is shared (rules/item-write-edit.rules)
   *  so the listing rows and the multi-row save build the same full-state body; what stays
   *  here is which state this surface holds — the baseline, the archive intent and the two
   *  tokens, which move together when a conflict is resolved. */
  private buildEditRequest(): IEditItemRequest {
    return editRules.buildEditRequest(this.fg.getRawValue(), {
      origin: this.origin,
      lookups: this.attributeLookups,
      writableFields: this.writableFields,
      pricing: this.isPricingRole,
      isArchived: this.isArchived,
      expectedRowVersion: this.expectedRowVersion,
      expectedItemRowVersion: this.expectedItemRowVersion
    });
  }

  /** Route a ProblemDetails failure by its stable `code` (design doc 8.1): ring the mapped
   *  control where there is one, and surface a client-owned message in the banner. The
   *  server message/detail is diagnostic only, so unknown or unmapped codes fall back to a
   *  generic line rather than echoing server text. */
  private applyServerError(err: HttpErrorResponse): void {
    this.applyProblem((err?.error ?? {}) as IItemProblemDetails, err?.status);
  }

  /** Open on the row's 400 (`refusal`). The banner and the waiting Save from the first frame;
   *  the rings once more after it, because the view binding a control to the form re-runs its
   *  validators, which drops a `server` error set by hand before the binding - and the
   *  controls bind on the view's first check, or on the next one for a tier the edit added.
   *  Not again once a change has cleared it. */
  private applyRefusal(refusal: IItemProblemDetails): void {
    this.applyProblem(refusal, 400);
    setTimeout(() => {
      if (this.serverRefused) {
        this.applyProblem(refusal, 400);
      }
    });
  }

  /** applyServerError by the ProblemDetails and the status alone: also how the popup opens on a
   *  refused listing row (`refusal`), which has no HttpErrorResponse to hand. */
  private applyProblem(problem: IItemProblemDetails, status: number | undefined): void {
    const info = copy.errorFor(problem.code, problem.data);

    if (info?.control) {
      const control = this.fg.get(info.control);
      control?.setErrors({ server: true });
      control?.markAsTouched();
      // the category ring keys off the main select's touched state
      if (info.control === 'categoryId') {
        this.fg.get('categoryMainId')?.markAsTouched();
      }
    }

    // The cost-margin 400 names its tier by From Quantity (HUBX-8466): the tier's price is
    // what has to change, so it is ringed until it is typed again.
    if (info?.dqFromQty !== undefined) {
      const tier = this.dqControls.controls.find(t => Number(t.get('fromQty')?.value) === info.dqFromQty);
      tier?.get('price')?.setErrors({ server: true });
      tier?.markAsTouched();
    }

    // 412 (HUBX-8439). It gets the amber band and the rings, never the red error band:
    // one message about a conflict, and it carries the way out. Returning here is what
    // keeps `serverError` unset, so the two do not stack.
    //
    // The flag is set whether or not `currentState` came. Without it there are no rings,
    // but the tokens are stale all the same and Review changes is still the answer - it
    // re-reads rather than applying the payload, so it works either way.
    if (problem.code === EDIT_ERROR_CODES.concurrencyConflict) {
      this.conflictSeen = true;
      if (problem.currentState) {
        this.applyConflict(problem.currentState);
      }
      if (this.target) {
        this.conflicted.emit({ id: this.target.id, currentState: problem.currentState });
      }
      return;
    }

    // The XID lock (HUBX-8458). Unlike the 412 this does NOT return early: it takes the
    // ordinary red banner and the ordinary ring, and the flag only adds the Revert link
    // beside them. Set here rather than in the shared catalog because that table carries
    // copy, not behaviour - and every other code in it needs no affordance at all.
    this.xidLockedInB1 = problem.code === EDIT_ERROR_CODES.hubxItemCodeLockedInB1;

    this.serverError = info?.message ?? copy.genericWriteError(this.isEdit);
    // A 400 is the server refusing the data: Save waits for a change (HUBX-8466). Any other
    // failure - a 5xx, the network, a check that did not run - is Save's to retry.
    this.serverRefused = status === 400;
  }

  /** The dialog stops click propagation so nothing inside it reaches the document
   *  (modal isolation). That also starves any document-level click-outside listener,
   *  so the company picker is closed here instead: any click that lands outside the
   *  picker's own container (`.clone-target`) closes it. The toggle and the search box
   *  live inside that container and keep their own behaviour; an option click has
   *  already closed the panel by the time it bubbles here. */
  onDialogClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.companyPickerOpen && !(event.target as HTMLElement).closest('.clone-target')) {
      this.companyPickerOpen = false;
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
